import MaskedTextField from '@/common/components/MaskedTextField'
import { Calendar } from '@/common/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/components/ui/popover'
import { replacePeriodToUnderscore } from '@/common/utils/transformers'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'

export interface DateInputProps {
  fieldName: string
  control: Control<any>
  setValue: UseFormSetValue<any>
  errors: FieldErrors<any>
  register: UseFormRegister<any>
  label?: string
  markAsRequired?: boolean
  className?: string
  disabled?: boolean
  mask?: string
  placeholder?: string
  errorMessage?: string
}

export const DateInput = ({
  fieldName,
  control,
  errors,
  label,
  markAsRequired = false,
  className,
  disabled = false,
  mask = '00/00/0000',
  placeholder = 'DD/MM/AAAA',
  errorMessage,
}: DateInputProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <MaskedTextField
          LeftIcon={
            <Popover
              open={isPopoverOpen}
              onOpenChange={(open) => {
                setIsPopoverOpen(open)
              }}
            >
              <PopoverTrigger
                data-testid={`${replacePeriodToUnderscore(fieldName)}_calendar_trigger`}
                asChild
                onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              >
                <button type="button" className="flex items-center justify-center w-6 h-6 cursor-pointer">
                  <CalendarIcon strokeWidth={1} className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#212023] border-neutral-op-8 dark" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date: Date | undefined) => {
                    setSelectedDate(date)
                    if (date) {
                      const formattedDate = format(date, 'dd/MM/yyyy')
                      field.onChange(formattedDate)
                    } else {
                      field.onChange('')
                    }
                    setIsPopoverOpen(false)
                  }}
                  className="w-auto"
                />
              </PopoverContent>
            </Popover>
          }
          label={label}
          markAsRequired={markAsRequired}
          mask={mask}
          placeholder={placeholder}
          className={className}
          errorMessage={(errors[fieldName]?.message as string | undefined) || errorMessage}
          name={fieldName}
          value={field.value || ''}
          onChange={(e) => field.onChange(e.target.value)}
          disabled={disabled}
          onFocus={() => setIsPopoverOpen(true)}
        />
      )}
    />
  )
}
